// ─── Balance Sheet Utility Functions ────────────────────────────────────────
// Pure computation helpers for balance sheet data stored in Supabase.
// No hardcoded financial data — all values come from the database.

// ── Category groupings for computing totals ──────────────────────────────
const CASH_KEYS = ['cashOnHand', 'checking', 'savings', 'tBills'];
const BROKERAGE_KEYS = ['robinhood', 'fundrise'];
const TAX_ADV_KEYS = ['roth401k', 'traditionalIra', 'rothIra', 'hsa'];
const PROPERTY_KEYS = ['vehicle'];
const OTHER_ASSET_KEYS = ['lifeInsurance', 'loyaltyPoints', 'accountsReceivable', 'securityDeposit', 'ownershiftEquity'];
const CREDIT_KEYS = ['creditCards', 'loansFromFriends'];
const LOAN_KEYS = ['vehicleLoan'];
export const CONTRIB_KEYS = ['contribRothIra', 'contribHsa', 'contrib401k', 'contribBrokerage'];

/** Convert '2022-12' → 'Dec-22' */
export function formatMonthKeyShort(key) {
  const [year, month] = key.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(month, 10) - 1]}-${year.slice(2)}`;
}

/** Convert '2022-08' → 'Aug 2022' */
export function formatMonthKey(key) {
  const [year, month] = key.split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[parseInt(month, 10) - 1]} ${year}`;
}

/** Convert '2022-08' → 'August 2022' (long) */
export function formatMonthKeyLong(key) {
  const [year, month] = key.split('-');
  const names = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  return `${names[parseInt(month, 10) - 1]} ${year}`;
}

/**
 * Compute category subtotals from a DB record (store row).
 * Uses stored totalAssets/totalLiabilities/netWorth when present.
 */
export function computeSubtotals(record) {
  const val = (k) => record[k] || 0;
  const sum = (keys) => keys.reduce((s, k) => s + val(k), 0);

  const cashTotal = sum(CASH_KEYS);
  const brokerageTotal = sum(BROKERAGE_KEYS);
  const taxAdvTotal = sum(TAX_ADV_KEYS);
  const investmentsTotal = brokerageTotal + taxAdvTotal;
  const propertyTotal = sum(PROPERTY_KEYS);
  const otherAssetsTotal = sum(OTHER_ASSET_KEYS);

  const computedAssets = cashTotal + investmentsTotal + propertyTotal + otherAssetsTotal;
  const totalAssets = record.totalAssets && Math.abs(record.totalAssets - computedAssets) > 0.01
    ? record.totalAssets
    : computedAssets;

  const ccTotal = sum(CREDIT_KEYS);
  const loansTotal = sum(LOAN_KEYS);
  const computedLiabilities = ccTotal + loansTotal;
  const totalLiabilities = record.totalLiabilities && Math.abs(record.totalLiabilities - computedLiabilities) > 0.01
    ? record.totalLiabilities
    : computedLiabilities;

  const netWorth = record.netWorth && Math.abs(record.netWorth - (totalAssets - totalLiabilities)) > 0.01
    ? record.netWorth
    : totalAssets - totalLiabilities;

  const contribTotal = (record.contribRothIra || 0) + (record.contribHsa || 0) + (record.contrib401k || 0) + (record.contribBrokerage || 0);

  return {
    ...record,
    cashTotal,
    brokerageTotal,
    taxAdvTotal,
    investmentsTotal,
    propertyTotal,
    otherAssetsTotal,
    totalAssets,
    ccTotal,
    loansTotal,
    totalLiabilities,
    netWorth,
    contribTotal,
    _zero: 0,
  };
}

/** Compute YTD contribution totals from DB data only. */
export function computeYtdContributions(balanceSheetMonthly, year) {
  const yearPrefix = `${year}-`;
  const ytd = {};
  CONTRIB_KEYS.forEach((k) => { ytd[k] = 0; });

  balanceSheetMonthly.forEach((r) => {
    if (r.month && r.month.startsWith(yearPrefix)) {
      CONTRIB_KEYS.forEach((k) => { ytd[k] += r[k] || 0; });
    }
  });

  ytd.total = CONTRIB_KEYS.reduce((s, k) => s + ytd[k], 0);
  return ytd;
}
